import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Button,
  ModalBody,
  ModalHeader,
  OffcanvasBody,
  OffcanvasHeader,
} from "reactstrap";
import {
  AppModal as Modal,
  AppOffcanvas as Offcanvas,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { showLoading } from "react-global-loading";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import { createReviewTemplateDraftVersion } from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import "../Standards/Standards.scss";
import "./TemplateList.scss";

const formatVersion = (template) => {
  if (!template) return "—";
  const label = template.currentVersion?.versionLabel;
  if (label) return label.replace(/^v/i, "");
  const versionNumber = template.currentVersion?.versionNumber;
  return versionNumber === undefined || versionNumber === null
    ? "—"
    : `${versionNumber}.0`;
};

const statusInfo = (status) =>
  status === 1
    ? { label: "Active", tone: "active" }
    : { label: "Inactive", tone: "archived" };

const TemplateActions = ({
  template,
  onPreview,
  onEdit,
  onCreateDraft,
  onHistory,
  lifecycleActions,
}) => {
  const isPublished = Boolean(template.currentVersion?.isPublished);
  return (
    <ActionMenu
      label={template.name}
      actions={[
        {
          label: "Preview Template",
          icon: <i className="bx bx-show" />,
          onSelect: () => onPreview(template),
        },
        isPublished
          ? {
              label: template.draftVersionId
                ? `Edit draft v${
                    Number(template.currentVersion?.versionNumber || 0) + 1
                  }.0`
                : "Create Draft Version",
              icon: (
                <i
                  className={
                    template.draftVersionId ? "bx bx-pencil" : "bx bx-copy-alt"
                  }
                />
              ),
              onSelect: () => onCreateDraft(template),
            }
          : {
              label: "Edit Draft",
              icon: <i className="bx bx-pencil" />,
              onSelect: () => onEdit(template),
            },
        {
          label: "Version History",
          icon: <i className="bx bx-history" />,
          onSelect: () => onHistory(template),
          separatorBefore: true,
        },
        ...lifecycleActions,
      ]}
    />
  );
};

const TemplateList = ({
  createRequest = 0,
  refreshRequest = 0,
  label,
  pluralLabel,
  listTemplates,
  DetailComponent,
  ViewComponent,
  StudioComponent,
  detailIdProp = "detailId",
  fileName,
}) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [detailId, setDetailId] = useState(0);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isStudioOpen, setIsStudioOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showStudioHistory, setShowStudioHistory] = useState(false);
  const [draftVersionConfirmation, setDraftVersionConfirmation] =
    useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  useEffect(() => {
    if (!isStudioOpen || !selectedTemplate) return;
    navigate(`/system/templates/review/${selectedTemplate.id}/edit`, {
      state: { template: selectedTemplate, initialHistory: showStudioHistory },
    });
  }, [isStudioOpen, selectedTemplate, showStudioHistory, navigate]);

  const loadData = () => {
    showLoading(true);
    listTemplates()
      .then((response) => {
        if (response.status) setTemplates(response.data || []);
        else ErrorNotify(response.message);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify(
          `Error occurred while fetching ${pluralLabel.toLowerCase()}.`
        );
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  const closeDetail = () => {
    setIsDetailOpen(false);
    setDetailId(0);
    loadData();
  };

  const openView = (id) => {
    setDetailId(id);
    setIsViewOpen(true);
  };

  const closeView = () => {
    setIsViewOpen(false);
    setDetailId(0);
    loadData();
  };

  const openTemplate = (template) => {
    if (
      StudioComponent &&
      template.currentVersion?.isPublished &&
      !template.draftVersionId
    )
      openPreview(template);
    else if (StudioComponent) {
      setSelectedTemplate({
        ...template,
        editingVersionId: template.draftVersionId || template.currentVersionId,
      });
      setShowStudioHistory(false);
      setIsStudioOpen(true);
    } else openView(template.id);
  };

  const openPreview = (template) => {
    setPreviewTemplate(template);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewTemplate(null);
  };

  const editPreviewTemplate = (template) => {
    setIsPreviewOpen(false);
    setPreviewTemplate(null);
    if (template.currentVersion?.isPublished) requestDraftVersion(template);
    else {
      setSelectedTemplate(template);
      setShowStudioHistory(false);
      setIsStudioOpen(true);
    }
  };

  const editDuplicatedTemplate = (template) => {
    setIsPreviewOpen(false);
    setPreviewTemplate(null);
    setSelectedTemplate(template);
    setIsStudioOpen(true);
    loadData();
  };

  const closeStudio = () => {
    setIsStudioOpen(false);
    setSelectedTemplate(null);
    loadData();
  };

  const createDraftVersion = async (template) => {
    showLoading(true);
    try {
      const response = await createReviewTemplateDraftVersion(template.id);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to create an editable draft version."
        );
      const draftTemplate = {
        ...template,
        ...(response.data || {}),
        editingVersionId: response.data?.draftVersionId,
      };
      setSelectedTemplate(draftTemplate);
      setShowStudioHistory(false);
      setIsPreviewOpen(false);
      setPreviewTemplate(null);
      setIsStudioOpen(true);
      SuccessNotify(response.message || "Draft version created.");
      loadData();
    } catch (error) {
      console.error(error);
      ErrorNotify(
        error.message || "Unable to create an editable draft version."
      );
    } finally {
      showLoading(false);
    }
  };

  const requestDraftVersion = (template) => {
    if (template.draftVersionId) {
      createDraftVersion(template);
      return;
    }
    setDraftVersionConfirmation(template);
  };

  const confirmDraftVersion = () => {
    const template = draftVersionConfirmation;
    setDraftVersionConfirmation(null);
    if (template) createDraftVersion(template);
  };

  const openVersionHistory = (template) => {
    setSelectedTemplate(template);
    setShowStudioHistory(true);
    setIsStudioOpen(true);
  };
  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (refreshRequest) loadData();
  }, [refreshRequest]);
  useEffect(() => {
    if (createRequest) {
      setDetailId(0);
      setIsDetailOpen(true);
    }
  }, [createRequest]);

  const counts = useMemo(
    () => ({
      total: templates.length,
      active: templates.filter((item) => item.status === 1).length,
      inactive: templates.filter((item) => item.status !== 1).length,
      versioned: templates.filter((item) => item.currentVersionId).length,
    }),
    [templates]
  );
  const filteredTemplates = useMemo(
    () =>
      templates.filter(
        (item) =>
          `${item.name || ""} ${item.description || item.address || ""} ${
            item.reviewTypeName || ""
          }`
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (status === "all" || String(item.status) === status)
      ),
    [templates, search, status]
  );
  const columns = [
    {
      id: "name",
      header: "Template",
      accessorKey: "name",
      minSize: 220,
      flex: 1.15,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          style={{ fontSize: 13 }}
          onClick={() => openTemplate(row.original)}
        >
          <span>{(getValue() || "T").charAt(0)}</span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorFn: (row) => row.description || row.address || "—",
      minSize: 360,
      flex: 2,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue()}</span>
      ),
    },
    {
      id: "version",
      header: "Current Version",
      accessorFn: formatVersion,
      minSize: 145,
      flex: 0.75,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => statusInfo(row.status).label,
      minSize: 110,
      flex: 0.65,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "reviewTypeName",
      header: "Review Type",
      accessorFn: (row) =>
        row.reviewTypeName ||
        (row.reviewTypeId ? "Unavailable review type" : "Not configured"),
      minSize: 190,
      flex: 1,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 80,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <TemplateActions
          template={row.original}
          onPreview={openPreview}
          onEdit={openTemplate}
          onCreateDraft={requestDraftVersion}
          onHistory={openVersionHistory}
          lifecycleActions={lifecycle.actions("review-template", row.original)}
        />
      ),
    },
  ];
  const detailProps = { [detailIdProp]: detailId, closeAction: closeDetail };
  const viewProps = { [detailIdProp]: detailId, closeAction: closeView };

  return (
    <section className="standards-page__content template-list">
      <SummaryCards
        items={[
          [
            "bx-file",
            "blue",
            `Total ${pluralLabel}`,
            counts.total,
            `All ${pluralLabel.toLowerCase()}`,
          ],
          [
            "bx-check-circle",
            "green",
            `Active ${pluralLabel}`,
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-pause-circle",
            "orange",
            `Inactive ${pluralLabel}`,
            counts.inactive,
            `${
              counts.total
                ? Math.round((counts.inactive / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-git-branch",
            "red",
            "Versioned Templates",
            counts.versioned,
            "Templates with a current version",
          ],
        ]}
        ariaLabel="Page summary"
      />
      <React.Fragment>
        
        <CustomDataGrid
 filterContent={<section
          className="standards-filter-panel"
          aria-label={`${pluralLabel} filters`}
        >
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={`Search ${pluralLabel.toLowerCase()}...`}
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >Clear</button>
          </div>
        </section>}

          data={filteredTemplates}
          columns={columns}
          onRefresh={loadData}
          fileName={fileName}
          recordLabel={pluralLabel.toLowerCase()}
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { fontSize: 14, borderTopColor: "#e7ebf2" },
          }}
        />
      </React.Fragment>
      {DetailComponent && (
        <Offcanvas
          backdrop="static"
          className="standards-drawer"
          direction="end"
          isOpen={isDetailOpen}
          toggle={closeDetail}
        >
          <OffcanvasHeader toggle={closeDetail} className="canvasHeader">
            New {label}
          </OffcanvasHeader>
          <OffcanvasBody>
            <DetailComponent {...detailProps} />
          </OffcanvasBody>
        </Offcanvas>
      )}
      {ViewComponent && (
        <Modal isOpen={isViewOpen} toggle={closeView} fullscreen>
          <ModalHeader toggle={closeView} className="canvasHeader">
            {label} Details
          </ModalHeader>
          <ModalBody>
            <ViewComponent {...viewProps} />
          </ModalBody>
        </Modal>
      )}
      {StudioComponent && isPreviewOpen && (
        <StudioComponent
          template={previewTemplate}
          closeAction={closePreview}
          initialPreview
          previewOnly
          onEditTemplate={editPreviewTemplate}
          onDuplicateTemplate={editDuplicatedTemplate}
        />
      )}
      <Modal
        isOpen={Boolean(draftVersionConfirmation)}
        toggle={() => setDraftVersionConfirmation(null)}
        centered
        className="review-template-stage-delete review-template-version-confirm"
        aria-label="Create draft version"
      >
        <ModalBody>
          <header>
            <span>
              <i className="bx bx-git-branch" />
            </span>
            <div>
              <h2>Create draft version</h2>
              <p>
                Create draft v
                {Number(
                  draftVersionConfirmation?.currentVersion?.versionNumber || 0
                ) + 1}
                .0 from published v{formatVersion(draftVersionConfirmation)}?
              </p>
            </div>
          </header>
          <p>
            Existing engagements will continue using v
            {formatVersion(draftVersionConfirmation)}. You can edit the draft
            without changing the published version.
          </p>
          <footer>
            <Button
              type="button"
              outline
              onClick={() => setDraftVersionConfirmation(null)}
            >
              Cancel
            </Button>
            <Button type="button" color="primary" onClick={confirmDraftVersion}>
              Create draft
            </Button>
          </footer>
        </ModalBody>
      </Modal>
      {lifecycle.dialog}
    </section>
  );
};

export default TemplateList;
